import React from 'react';
import { usePlayer } from '../context/PlayerContext';
import { fileUrl } from '../lib/api';
import { Play, Pause, SkipBack, SkipForward, Volume2 } from 'lucide-react';
import { Waveform } from './Waveform';

const formatTime = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

export const AudioPlayer = () => {
  const { currentTrack, isPlaying, progress, duration, volume, togglePlay, seek, changeVolume, next, prev } = usePlayer();

  if (!currentTrack) return null;

  const coverSrc = currentTrack.cover_path
    ? fileUrl(currentTrack.cover_path)
    : 'https://images.unsplash.com/photo-1614613535308-eb5fbd3d2c17?crop=entropy&cs=srgb&fm=jpg&q=85&w=200';

  return (
    <div
      className="glass-bottom fixed bottom-0 left-0 right-0 z-50 px-4 py-3 md:px-6"
      data-testid="audio-player"
    >
      <div className="max-w-[1800px] mx-auto flex items-center gap-3 md:gap-6">
        {/* Track info */}
        <div className="flex items-center gap-3 min-w-0 flex-shrink-0 w-[200px] md:w-[280px]">
          <img
            src={coverSrc}
            alt=""
            className="w-12 h-12 md:w-14 md:h-14 rounded-lg object-cover flex-shrink-0"
          />
          <div className="min-w-0 hidden sm:block">
            <div className="text-white text-sm font-bold truncate font-heading" data-testid="player-title">
              {currentTrack.title}
            </div>
            <div className="text-[#A1A1AA] text-xs truncate">
              {currentTrack.author?.name || 'Unknown'}
            </div>
          </div>
        </div>

        {/* Controls + waveform */}
        <div className="flex-1 flex flex-col items-center gap-1 min-w-0">
          <div className="flex items-center gap-4">
            <button
              onClick={prev}
              className="text-[#A1A1AA] hover:text-white transition-colors"
              data-testid="player-prev"
              aria-label="Previous"
            >
              <SkipBack size={20} />
            </button>
            <button
              onClick={togglePlay}
              className="w-10 h-10 rounded-full bg-[#FFB000] text-black flex items-center justify-center hover:bg-[#E69E00] transition-colors active:scale-95"
              data-testid="player-play-pause"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause size={18} fill="black" /> : <Play size={18} fill="black" />}
            </button>
            <button
              onClick={next}
              className="text-[#A1A1AA] hover:text-white transition-colors"
              data-testid="player-next"
              aria-label="Next"
            >
              <SkipForward size={20} />
            </button>
          </div>
          <div className="w-full flex items-center gap-2">
            <span className="text-[10px] text-[#A1A1AA] font-mono flex-shrink-0 w-10 text-right">
              {formatTime(progress)}
            </span>
            <div className="flex-1 min-w-0">
              <Waveform
                isPlaying={isPlaying}
                progress={progress}
                duration={duration}
                bars={48}
                seekable
                onSeek={seek}
              />
            </div>
            <span className="text-[10px] text-[#A1A1AA] font-mono flex-shrink-0 w-10">
              {formatTime(duration)}
            </span>
          </div>
        </div>

        {/* Volume */}
        <div className="hidden lg:flex items-center gap-2 w-[140px]">
          <Volume2 size={18} className="text-[#A1A1AA]" />
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => changeVolume(parseFloat(e.target.value))}
            className="flex-1 accent-[#FFB000]"
            data-testid="player-volume"
          />
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
